import {
  CheckCircleFilled,
  MessageOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import {
  BackBar,
  billsCount,
  daysCount,
  describeError,
  dirhams,
  fullDate,
  monthYear,
  present,
} from '@horof/shared';
import { Alert, App, Button, Empty, Skeleton, Tag } from 'antd';
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from 'urql';
import {
  RecordReminderDocument,
  type Reminder,
  RemindersDueDocument,
} from './reports-data';

/**
 * Today's reminders, as arqam lists them (an unpaid bill older than the first
 * reminder delay, no reminder within the interval, not opted out). A tap on
 * WhatsApp or SMS opens the ready message on this phone and records the
 * reminder (arqam doc 14), which takes the member off today's list.
 */
export function RemindersPage() {
  const { message } = App.useApp();
  const [{ data, error }, refetch] = useQuery({
    query: RemindersDueDocument,
    requestPolicy: 'network-only',
  });
  const [, record] = useMutation(RecordReminderDocument);
  // Recorded during this visit: shown as done until the list is refreshed.
  const [done, setDone] = useState<Set<string>>(new Set());

  const reminders = present(data?.remindersDue?.nodes);

  function recordSent(r: Reminder, channel: 'WHATSAPP' | 'SMS') {
    setDone((d) => new Set(d).add(r.memberId));
    // Not awaited: the link opens the message right away; the log follows.
    record({
      memberId: r.memberId,
      channel,
      message: channel === 'WHATSAPP' ? r.whatsappMessage : r.smsMessage,
    }).then((result) => {
      if (result.error) {
        setDone((d) => {
          const next = new Set(d);
          next.delete(r.memberId);
          return next;
        });
        message.error(describeError(result.error, 'لم يُسجَّل التذكير.'));
      }
    });
  }

  return (
    <div className="page">
      <BackBar to="/reports" label="التقارير" />
      <h1 className="member-title">تذكيرات اليوم</h1>
      <div className="field-hint">
        اضغط «واتساب» أو «رسالة قصيرة»: تُفتح الرسالة جاهزة على هاتفك، أرسلها.
        يُسجَّل التذكير ولا يعود العضو إلى القائمة قبل الموعد المقبل.
      </div>
      {error && <Alert type="error" showIcon message="تعذّر تحميل القائمة" />}
      {!data && !error && <Skeleton active paragraph={{ rows: 8 }} />}
      {data && reminders.length === 0 && (
        <Empty description="لا أحد يجب تذكيره اليوم" />
      )}

      <ul className="plain-list reminder-list">
        {reminders.map((r) => {
          const sent = done.has(r.memberId);
          return (
            <li key={r.memberId} className={`card${sent ? ' is-done' : ''}`}>
              <div className="bill-head">
                <Link to={`/members/${r.memberId}`} className="member-name">
                  {r.fullName}
                </Link>
                {r.kind === 'LATE_NOTICE' ? (
                  <Tag color="red">متأخر {daysCount(r.daysOverdue)}</Tag>
                ) : (
                  <Tag color="orange">تذكير</Tag>
                )}
              </div>
              <div>
                <strong>{dirhams(r.amountDue)}</strong> ·{' '}
                {billsCount(r.billsCount)}
                {r.oldestBillMonth && ` منذ ${monthYear(r.oldestBillMonth)}`}
              </div>
              <div className="field-hint">
                {r.lastRemindedAt
                  ? `آخر تذكير: ${fullDate(r.lastRemindedAt)} (${r.remindersSent})`
                  : 'لم يُذكَّر من قبل'}
              </div>
              {sent ? (
                <div className="sent-line">
                  <CheckCircleFilled /> سُجّل التذكير
                </div>
              ) : r.whatsappUrl || r.smsUrl ? (
                <div className="document-actions">
                  {r.whatsappUrl && (
                    <Button
                      href={r.whatsappUrl}
                      target="_blank"
                      icon={<WhatsAppOutlined />}
                      className="whatsapp-button"
                      onClick={() => recordSent(r, 'WHATSAPP')}
                    >
                      واتساب
                    </Button>
                  )}
                  {r.smsUrl && (
                    <Button
                      href={r.smsUrl}
                      icon={<MessageOutlined />}
                      type={r.notifyChannel === 'SMS' ? 'primary' : 'default'}
                      onClick={() => recordSent(r, 'SMS')}
                    >
                      رسالة قصيرة
                    </Button>
                  )}
                </div>
              ) : (
                <div className="field-hint">
                  لا رقم هاتف محمول صالح: اتصل به أو اطبع{' '}
                  <a href={r.noticeLink} target="_blank" rel="noreferrer">
                    إشعار الأداء
                  </a>
                  ، أو <Link to={`/members/${r.memberId}`}>صحّح رقمه</Link>.
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {done.size > 0 && (
        <Button
          size="large"
          onClick={() => {
            setDone(new Set());
            refetch({ requestPolicy: 'network-only' });
          }}
        >
          تحديث القائمة
        </Button>
      )}
    </div>
  );
}
